import type { Vec2 } from '@4bitlabs/vec2';

import { type DistortPolyOptions, distortPolygon } from './distort-poly.js';
import { createGaussianRng, type RandomNumberGenerator } from './rng.js';
import { simplify } from './simplify.js';
import { simplifyWithWeights } from './simplify-with-weights.js';
import type { PointsAndWeights } from './types.js';
import { type WindingOrder, windingOrderOf } from './winding-order.js';

const nReduce = <T>(
  length: number,
  fn: (prev: T, index: number) => T,
  initialValue: T,
) =>
  Array.from({ length }).reduce<T>(
    (prev, _, index) => fn(prev, index),
    initialValue,
  );

const ones = (length: number) => Array.from<number>({ length }).fill(1);

export interface WatercolorizeOptions {
  preEvolutions?: number;
  evolutions?: number;
  layersPerEvolution?: number;
  layerEvolutions?: number;
  vertexWeights?: number[];
  blurWeightsOnDistort?: boolean;
  simplifyAfterPreEvolution?: number | false;
  simplifyEachEvolution?: number | false;
  random?: RandomNumberGenerator;
  windingOrder?: WindingOrder;
}

export function* watercolorize(
  points: Vec2[],
  options: WatercolorizeOptions = {},
): Generator<Vec2[]> {
  const {
    preEvolutions = 0,
    evolutions = 5,
    layersPerEvolution = 3,
    layerEvolutions = 3,
    vertexWeights = ones(points.length),
    blurWeightsOnDistort = false,
    simplifyAfterPreEvolution = false,
    simplifyEachEvolution = false,
    random = Math.random,
    windingOrder = windingOrderOf(points),
  } = options;

  const gaussRng = createGaussianRng(random);
  const distortPolyOptions: DistortPolyOptions = {
    blurWeightsOnDistort,
    windingOrder,
    gaussRng,
  };

  const distort = (_: PointsAndWeights) =>
    distortPolygon(_, distortPolyOptions);

  let prev: PointsAndWeights = [points, vertexWeights];
  prev = nReduce(preEvolutions, distort, prev);

  if (preEvolutions > 0 && simplifyAfterPreEvolution)
    prev = simplifyWithWeights(prev, simplifyAfterPreEvolution);

  for (let e = 0; e < evolutions; e++) {
    for (let l = 0; l < layersPerEvolution; l++) {
      const [layerPoints] = nReduce<PointsAndWeights>(
        layerEvolutions,
        distort,
        distort(prev),
      );

      yield simplify([...layerPoints, layerPoints[0]], 1).slice(0, -1);
    }
    prev = distort(prev);
    if (simplifyEachEvolution)
      prev = simplifyWithWeights(prev, simplifyEachEvolution);
  }
}
